import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, ArrowRight, Coins, Shield, Zap, Info } from "lucide-react";
import TopBar from "@/components/layout/TopBar";
import Sidebar from "@/components/layout/Sidebar";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { useAuthModal } from "@/context/SupabaseAuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ArrowLeftRight } from "lucide-react";

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const { setShowLogin } = useAuthModal();
  const { currency, toggleCurrency, formatPrice } = useCurrency();

  // Source of truth for pricing is ZAR; CurrencyContext converts for display.
  // Keep these aligned with `PurchaseCredits.tsx` and `ProfessionalPlan.tsx`.
  const ZAR_PRICE_PER_CREDIT = 44.85;
  const ZAR_PROFESSIONAL_PER_YEAR = 1157.97;

  const handleCreditsClick = () => {
    if (user) {
      navigate("/purchase-credits");
    } else {
      setShowLogin(true);
    }
  };

  const handleProfessionalClick = () => {
    if (user) {
      navigate("/professional");
    } else {
      setShowLogin(true);
    }
  };

  return (
    <div className="min-h-screen bg-background flex relative overflow-x-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-y-auto">
        <TopBar title="Pricing" />

        <main className="flex-1 p-4 md:p-8 lg:p-12 relative flex flex-col">
          {/* Background Decoration */}
          <div
            className="absolute inset-0 opacity-[.05] dark:opacity-[.02] pointer-events-none"
            style={{
              backgroundImage: "url(/map.webp)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          />

          <div className="container mx-auto max-w-6xl space-y-12 relative z-10 py-8 lg:py-16">
            <div className="text-center space-y-6 mb-8">
              <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl mb-8">
                Pricing for environmental intelligence at every scale
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mt-6">
                From one-off environmental reports to professional-grade
                monitoring and forecasting.
              </p>

              {/* Currency toggle for unauthenticated users too */}
              <div className="flex justify-center mt-4">
                <Button
                  variant="outline"
                  onClick={toggleCurrency}
                  className="gap-2"
                >
                  <ArrowLeftRight className="h-4 w-4" />
                  Prices in {currency === "ZAR" ? "🇺🇸 USD" : "🇿🇦 ZAR"}
                </Button>
              </div>
              {currency === "USD" && (
                <p className="text-xs text-muted-foreground mt-2">
                  * indicative, ~R18.50 per $1
                </p>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-stretch max-w-5xl mx-auto">
              {/* PAYG Option */}
              <Card className="flex flex-col border-2 border-border/50 hover:border-primary/50 transition-all duration-300 shadow-sm hover:shadow-md">
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <Badge
                      variant="outline"
                      className="bg-primary/5 text-primary border-primary/20"
                    >
                      Flexibility
                    </Badge>
                  </div>
                  <CardTitle className="text-3xl flex items-center gap-2">
                    <Coins className="h-6 w-6 text-primary" />
                    Pay-As-You-Go
                  </CardTitle>
                  <CardDescription className="text-lg">
                    Perfect for occasional reports and data monitoring.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1">
                  <div className="text-4xl font-bold mb-2">
                    {formatPrice(ZAR_PRICE_PER_CREDIT)}{" "}
                    <span className="text-lg font-normal text-muted-foreground">
                      / credit
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-6">
                    Typically used for a single environmental report or
                    prediction run.
                  </p>
                  <ul className="space-y-3">
                    {[
                      "No monthly commitment",
                      "Buy credits only when you need them",
                      "Bulk discounts starting at 10 credits",
                      "Credits never expire",
                      "Access to all standard features",
                    ].map((feature, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500 shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    className="w-full h-12 text-lg font-medium group"
                    onClick={handleCreditsClick}
                  >
                    Buy credits
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    {user ? "Use them anytime" : "Sign up to get started"}
                  </p>
                </CardFooter>
              </Card>

              {/* Professional Plan Option */}
              <Card className="flex flex-col border-2 border-primary shadow-lg relative bg-primary/[0.02]">
                <div className="absolute -top-3 right-4 z-20">
                  <Badge className="bg-primary text-primary-foreground px-3 py-1 text-sm font-bold shadow-md border-none pointer-events-none cursor-default hover:bg-primary">
                    BEST VALUE
                  </Badge>
                </div>
                <CardHeader className="pt-8">
                  <div className="flex justify-between items-start mb-2">
                    <Badge
                      variant="outline"
                      className="bg-primary/10 text-primary border-primary/30"
                    >
                      Organization
                    </Badge>
                  </div>
                  <CardTitle className="text-3xl flex items-center gap-2">
                    <Shield className="h-6 w-6 text-primary" />
                    Professional
                  </CardTitle>
                  <CardDescription className="text-lg">
                    Optimized for experts and environmental organizations.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1">
                  <div className="text-4xl font-bold mb-1">
                    {formatPrice(ZAR_PROFESSIONAL_PER_YEAR)}{" "}
                    <span className="text-lg font-normal text-muted-foreground">
                      / year
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Less than {formatPrice(ZAR_PROFESSIONAL_PER_YEAR / 12)}/month
                    for professional-grade environmental analysis.
                  </p>
                  <div className="text-sm text-primary font-medium mb-2 flex items-center gap-1">
                    <Zap className="h-4 w-4 fill-primary" />
                    Includes 100 annual credits
                  </div>
                  <p className="text-xs text-muted-foreground mb-6">
                    Enough for approximately 100 standard reports per year.
                  </p>
                  <ul className="space-y-3">
                    {[
                      "Priority data processing",
                      "10-year environmental predictions",
                      "20-year historical data archive",
                      "High-resolution satellite imagery",
                      "Priority 24/7 technical support",
                      "White-label report branding",
                    ].map((feature, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500 shrink-0" />
                        <span className="text-sm font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="flex flex-col gap-3">
                  <Button
                    className="w-full h-12 text-lg font-medium hover:bg-[#09e0ce]/50 hover:text-white transition-colors duration-200 hover:border-primary/80"
                    onClick={handleProfessionalClick}
                  >
                    Start Professional Plan
                  </Button>
                  <p className="text-xs text-center text-muted-foreground px-4">
                    {user
                      ? "Annual billing · Cancel anytime"
                      : "Sign up to get started · Annual billing"}
                  </p>
                </CardFooter>
              </Card>
            </div>

            {/* Additional Information */}
            <div className="mt-16 text-center bg-muted/30 p-8 rounded-2xl border border-dashed border-border/60">
              <h3 className="text-lg font-semibold mb-4 flex items-center justify-center gap-2">
                <Info className="h-5 w-5 text-muted-foreground" />
                Need help choosing?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Start with Pay-As-You-Go to explore our environmental
                intelligence platform. Upgrade to Professional when you need
                advanced features and bulk reporting.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-sm">
                <Button
                  variant="link"
                  onClick={() => navigate("/terms-and-privacy")}
                  className="text-muted-foreground hover:text-foreground underline"
                >
                  Terms & Privacy Policy
                </Button>
              </div>
            </div>

            {/* Trust & Reassurance */}
            <div className="text-center text-sm text-muted-foreground mt-8">
              Secure payments · No hidden fees · Cancel anytime
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Pricing;
